import React, { JSX, useState, useEffect } from "react";

import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import AppLayout from "../components/layout/AppLayout";
import EditProfileModal from "../components/profile/EditProfileModal";
import "./ProfilePage.css";

export default function ProfilePage(): JSX.Element {
  const { user, activeDates, signOut } = useAuth();
  // Profile state for source of truth
  const [profile, setProfile] = useState<{ display_name: string | null; avatar_url: string | null } | null>(null);

  // Fetch profile data
  useEffect(() => {
    async function getProfile() {
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('display_name, avatar_url')
        .eq('id', user.id)
        .single();

      if (!error && data) {
        setProfile(data);
      }
    }

    getProfile();
  }, [user]);

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  // Get calendar data
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = currentMonth.toLocaleString("default", { month: "long" });

  const prevMonth = () => {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const targetMonth = new Date(year, month - 1, 1);
    if (targetMonth >= new Date(oneYearAgo.getFullYear(), oneYearAgo.getMonth(), 1)) {
      setCurrentMonth(targetMonth);
    }
  };

  const nextMonth = () => {
    const now = new Date();
    const isCurrentMonth = month === now.getMonth() && year === now.getFullYear();
    if (!isCurrentMonth) {
      setCurrentMonth(new Date(year, month + 1, 1));
    }
  };

  const isViewingCurrentMonth = month === new Date().getMonth() && year === new Date().getFullYear();

  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  const isAtOneYearLimit = year === oneYearAgo.getFullYear() && month === oneYearAgo.getMonth();

  // Generate calendar days
  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="calendar-day empty" />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    // Construct the date string for this day in the rendered month
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // Check if it's today
    const isToday =
      day === new Date().getDate() &&
      month === new Date().getMonth() &&
      year === new Date().getFullYear();

    // Check if it has posts (and is not today, to preserve blue for today)
    const hasPost = activeDates.has(dateString);

    calendarDays.push(
      <div
        key={day}
        className={`calendar-day ${isToday ? "active" : hasPost ? "has-post" : ""}`}
      >
        {day}
      </div>
    );
  }

  // Extract username from profile (source of truth) or metadata/email (fallback)
  const avatarUrl = profile?.avatar_url || user?.user_metadata?.avatar_url;
  const displayName = profile?.display_name || user?.user_metadata?.display_name || user?.email?.split("@")[0] || "User";

  const email = user?.email || "";



  return (
    <AppLayout>
      <div className="profile-page">
        <div className="profile-container">
          {/* Header */}
          <header className="profile-header">
            <button
              className="mobile-logout-button"
              onClick={() => signOut()}
              aria-label="Sign Out"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              <span>Log Out</span>
            </button>
            <h1 className="header-title">Profile</h1>
            <button
              className="profile-settings-button"
              onClick={() => setEditProfileOpen(true)}
              aria-label="Edit Profile"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
              <span>Settings</span>
            </button>
          </header>

          {/* Main Content */}
          <main className="profile-main">
            {/* User Info */}
            <div className="user-info">
              <div className="avatar">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Profile" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <span className="avatar-initial">{displayName.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <h2 className="user-name">{displayName}</h2>

            </div>

            {/* Gratitude Calendar */}
            <div className="calendar-section">
              <div className="calendar-board-card">


                <div className="calendar-paper">
                  <div className="calendar">
                    <div className="calendar-header">
                      <button
                        className="calendar-nav"
                        onClick={prevMonth}
                        disabled={isAtOneYearLimit}
                        style={{ opacity: isAtOneYearLimit ? 0.3 : 1, cursor: isAtOneYearLimit ? 'default' : 'pointer' }}
                      >&lt;</button>
                      <span className="calendar-month">{monthName} {year}</span>
                      <button
                        className="calendar-nav"
                        onClick={nextMonth}
                        disabled={isViewingCurrentMonth}
                        style={{ opacity: isViewingCurrentMonth ? 0.3 : 1, cursor: isViewingCurrentMonth ? 'default' : 'pointer' }}
                      >&gt;</button>
                    </div>

                    <div className="calendar-weekdays">
                      <span>S</span>
                      <span>M</span>
                      <span>T</span>
                      <span>W</span>
                      <span>T</span>
                      <span>F</span>
                      <span>S</span>
                    </div>

                    <div className="calendar-grid">
                      {calendarDays}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>


        <EditProfileModal
          open={editProfileOpen}
          onClose={() => setEditProfileOpen(false)}
          email={email}
          displayName={displayName}
          avatarUrl={avatarUrl}
        />
      </div>
    </AppLayout>
  );
}
